import React, { useState, useEffect, useMemo } from "react";
import { ImageList, ImageListItem } from "@mui/material";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "../api/firebase";

const isHttpUrl = (s) => typeof s === "string" && /^https?:\/\//i.test(s);

const ImageRenderer = ({ images }) => {

  // console.log("[ImageRenderer] images payload:", images);
  // console.trace("[ImageRenderer] render trace");


  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [resolvedImages, setResolvedImages] = useState([]);

  // 画像らしいものだけに絞る（file 選択データなどが混ざっても落ちないように）
  const normalized = useMemo(() => {
    const arr = Array.isArray(images) ? images : [];
    return arr
      .map((img) => {
        // Firestore の postImage: { url, path, ... }
        // アップロード選択系: { file, ... } などもあり得るのでガード
        const url = img?.url;
        const path = img?.path;
        return { ...img, url, path };
      })
      .filter((img) => typeof img?.url === "string" || typeof img?.path === "string");
  }, [images]);

  useEffect(() => {
    let canceled = false;

    const run = async () => {
      const promises = normalized.map(async (img) => {
        // 既に downloadURL ならそのまま
        if (isHttpUrl(img.url)) return { ...img, resolvedUrl: img.url };

        // Storage のパスは path を優先（なければ url をパス扱い）
        const storagePath = img.path || img.url;
        if (typeof storagePath !== "string" || storagePath.length === 0) {
          return { ...img, resolvedUrl: "" };
        }

        // localStorage キャッシュ（キーは storagePath の方が安全）
        const cacheKey = `imgcache:${storagePath}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached && isHttpUrl(cached)) {
          return { ...img, resolvedUrl: cached };
        }

        const dl = await getDownloadURL(ref(storage, storagePath));
        localStorage.setItem(cacheKey, dl);
        return { ...img, resolvedUrl: dl };
      });

      const out = await Promise.all(promises);
      if (!canceled) {
        setResolvedImages(out.filter((x) => isHttpUrl(x.resolvedUrl)));
      }
    };

    run().catch((e) => {
      console.error("ImageRenderer fetchImages error:", e);
      if (!canceled) setResolvedImages([]);
    });

    return () => {
      canceled = true;
    };
  }, [normalized]);

  if (!resolvedImages.length) return null;

  const handleImageClick = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  return (
    <>
      <ImageList variant="quilted" cols={4} gap={8}>
        {resolvedImages.map((img, index) => (
          <ImageListItem
            key={img?.path || img?.url || index}
            cols={index % 3 === 0 ? 2 : 1}
            rows={index % 3 === 0 ? 2 : 1}
            onClick={() => handleImageClick(index)}
            style={{ cursor: "pointer" }}
          >
            <img
              src={img.resolvedUrl}
              alt={`Image ${index + 1}`}
              loading="lazy"
              style={{ objectFit: "cover", width: "100%", height: "100%" }}
            />
          </ImageListItem>
        ))}
      </ImageList>

      <Lightbox
        open={isOpen}
        close={() => setIsOpen(false)}
        slides={resolvedImages.map((img) => ({ src: img.resolvedUrl }))}
        index={photoIndex}
        onIndexChange={setPhotoIndex}
      />
    </>
  );
};

export default ImageRenderer;
